import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription, firstValueFrom } from 'rxjs';
import { AppState } from 'src/app/redux/app.state';
import { NotificationService } from 'src/app/services/notification.service';
import {
  Product,
  ProductCellBalance,
  ProductStockSummary,
  StockCellMovementCellRef,
  StockCellMovementRow,
  User,
  VirtualCellOffsetPreview,
  Warehouse,
  WarehouseCell
} from 'src/typings';
import { ProductService } from '../product.service';
import { WarehouseService } from 'src/app/warehouse/warehouse.service';

@Component({
  selector: 'app-catalog-stock-dialog',
  templateUrl: './catalog-stock-dialog.component.html',
  styleUrls: ['./catalog-stock-dialog.component.scss']
})
export class CatalogStockDialogComponent implements OnInit, OnDestroy {
  product?: Product;
  warehouse?: Warehouse;
  warehouseCells: WarehouseCell[] = [];
  filteredTargetOptions: WarehouseCell[] = [];
  actionMode: 'PICK' | 'PLACE' | 'COUNT' = 'PICK';
  actionQty = 1;
  sourceCellId?: number | null;
  targetCellId?: number | null;
  targetCellControl = new FormControl<string>('', { nonNullable: true });
  loading = false;
  creatingTargetCell = false;
  currentUser?: User;
  movementRows: StockCellMovementRow[] = [];
  movementTotal = 0;
  movementLoading = false;
  movementLimit = 30;
  movementOffset = 0;
  virtualOffsetPreview?: VirtualCellOffsetPreview;
  virtualOffsetLoading = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly dialogRef: MatDialogRef<CatalogStockDialogComponent>,
    private readonly warehouseService: WarehouseService,
    private readonly productService: ProductService,
    private readonly notificationService: NotificationService,
    private readonly store: Store<AppState>
  ) {
    this.subscriptions.add(
      this.store.subscribe((state) => {
        this.currentUser = state.user.info;
        if (!this.canUseCountMode && this.actionMode === 'COUNT') {
          this.actionMode = 'PICK';
        }
      })
    );
    this.subscriptions.add(
      this.productService.productGw.subscribe((products) => {
        const updated = products.find((product) => product.id === this.product?.id);
        if (updated) {
          this.applyRealtimeProductUpdate(updated);
        }
      })
    );
    this.subscriptions.add(
      this.targetCellControl.valueChanges.subscribe((value) => {
        this.updateFilteredTargetOptions(value);
        this.syncTargetCellIdFromInput(value);
      })
    );
  }

  async ngOnInit() {
    await Promise.all([this.loadWarehouseCells(), this.reloadMovementRows()]);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  get warehouseSummary(): ProductStockSummary | undefined {
    if (!this.product || !this.warehouse?.id) return undefined;
    return this.product.stockSummary?.[String(this.warehouse.id)];
  }

  get balances(): ProductCellBalance[] {
    if (!this.product || !this.warehouse?.id) return [];
    return (this.product.cellBalances || [])
      .filter((row) => row.warehouseId === this.warehouse?.id)
      .sort((a, b) => {
        if (a.type === b.type) {
          return a.code.localeCompare(b.code);
        }
        if (a.type === 'REAL') return -1;
        if (b.type === 'REAL') return 1;
        if (a.type === 'BUFFER') return -1;
        if (b.type === 'BUFFER') return 1;
        return a.code.localeCompare(b.code);
      });
  }

  get realBalances(): ProductCellBalance[] {
    return this.balances.filter((row) => row.type === 'REAL');
  }

  get sourceOptions(): ProductCellBalance[] {
    return this.balances.filter((row) => row.qty > 0);
  }

  get targetOptions(): WarehouseCell[] {
    return this.warehouseCells.filter((row) => row.type === 'REAL' && row.isActive);
  }

  get isTargetCellMode() {
    return this.actionMode === 'PLACE' || this.actionMode === 'COUNT';
  }

  get targetCellQuery() {
    return this.normalizeCellCode(this.targetCellControl.value);
  }

  get canCreateTargetCell() {
    return (
      this.isTargetCellMode &&
      Boolean(this.warehouse?.id) &&
      Boolean(this.targetCellQuery) &&
      !this.findTargetOptionByCode(this.targetCellQuery) &&
      !this.creatingTargetCell &&
      !this.loading
    );
  }

  get canSubmitAction() {
    const qty = Number(this.actionQty || 0);
    if (!qty || qty <= 0 || this.loading) return false;
    if (this.actionMode === 'COUNT' && !this.canUseCountMode) return false;
    if (this.actionMode === 'PICK') return Boolean(this.sourceCellId);
    if (this.actionMode === 'PLACE') return Boolean(this.targetCellId);
    return Boolean(this.targetCellId);
  }

  get canUseCountMode() {
    return this.currentUser?.roles?.some((role) => role.description === 'admin') === true;
  }

  get recommendedCells() {
    return this.warehouseSummary?.recommendedCells || [];
  }

  get bufferQty() {
    return Number(this.warehouseSummary?.bufferQty || 0);
  }

  get addressDebtQty() {
    return Number(this.warehouseSummary?.unassignedOutQty || 0);
  }

  get bufferSummaryLabel() {
    return this.bufferQty < 0 ? 'Долг размещения' : 'Ожидает размещения';
  }

  get unassignedOutSummaryLabel() {
    return this.addressDebtQty < 0 ? 'Списано без снятия' : 'Снято, ожидает списания';
  }

  get hasResolvableVirtualOffset() {
    return this.bufferQty < 0 && this.addressDebtQty > 0;
  }

  get canLoadMoreMovements() {
    return this.movementRows.length < this.movementTotal;
  }

  selectMode(mode: 'PICK' | 'PLACE' | 'COUNT') {
    if (mode === 'COUNT' && !this.canUseCountMode) {
      this.notificationService.warning('Режим переучета доступен только администратору');
      return;
    }
    this.actionMode = mode;
    if (mode === 'PICK' && !this.sourceCellId) {
      this.sourceCellId = this.realBalances.find((row) => row.qty > 0)?.cellId || null;
    }
    if ((mode === 'PLACE' || mode === 'COUNT') && !this.targetCellId) {
      this.targetCellId =
        this.targetOptions.find((row) => this.recommendedCells.includes(row.code))?.id ||
        this.targetOptions[0]?.id ||
        null;
    }
    if (mode === 'PLACE' || mode === 'COUNT') {
      this.syncTargetCellControl();
    }
  }

  prefillPick(row: ProductCellBalance) {
    this.actionMode = 'PICK';
    this.sourceCellId = row.cellId;
    this.actionQty = Math.max(1, Number(row.qty || 0));
  }

  prefillPlace(row: ProductCellBalance) {
    this.actionMode = 'PLACE';
    this.targetCellId = row.cellId;
    this.actionQty = Math.max(1, Number(row.qty || 0));
    this.syncTargetCellControl();
  }

  prefillCount(row: ProductCellBalance) {
    if (!this.canUseCountMode) {
      this.notificationService.warning('Режим переучета доступен только администратору');
      return;
    }
    this.actionMode = 'COUNT';
    this.targetCellId = row.cellId;
    this.actionQty = Number(row.qty || 0);
    this.syncTargetCellControl();
  }

  getCellLabel(row: Pick<ProductCellBalance, 'code' | 'type' | 'qty'>) {
    if (row.type === 'BUFFER') {
      return `${row.qty < 0 ? 'Долг размещения' : 'Ожидает размещения'} (${row.qty})`;
    }
    if (row.type === 'UNASSIGNED_OUT') {
      return `${row.qty < 0 ? 'Списано без снятия' : 'Снято, ожидает списания'} (${row.qty})`;
    }
    return `${row.code} (${row.qty})`;
  }

  getWarehouseCellLabel(row: WarehouseCell) {
    return row.code;
  }

  onTargetCellSelected(event: MatAutocompleteSelectedEvent) {
    const code = this.normalizeCellCode(event.option.value);
    const match = this.findTargetOptionByCode(code);
    this.targetCellId = match?.id || null;
    if (match) {
      this.targetCellControl.setValue(match.code, { emitEvent: false });
      this.updateFilteredTargetOptions(match.code);
    }
  }

  getBalanceTypeLabel(row: ProductCellBalance) {
    if (row.type === 'REAL') return 'Полка';
    if (row.type === 'BUFFER') return row.qty < 0 ? 'Требует проверки' : 'Буфер прихода';
    if (row.type === 'UNASSIGNED_OUT') {
      return row.qty < 0 ? 'Требует снятия с полки' : 'Ожидает списания';
    }
    return row.type;
  }

  getBalanceCodeLabel(row: ProductCellBalance) {
    if (row.type === 'BUFFER') return row.qty < 0 ? 'Долг размещения' : 'Ожидает размещения';
    if (row.type === 'UNASSIGNED_OUT') {
      return row.qty < 0 ? 'Списано без снятия' : 'Снято, ожидает списания';
    }
    return row.code;
  }

  getMovementCellLabel(cell?: StockCellMovementCellRef | null) {
    if (!cell) return '-';
    if (cell.type === 'BUFFER') return 'Буфер размещения';
    if (cell.type === 'UNASSIGNED_OUT') return 'Неподтвержденное снятие';
    return cell.code || '-';
  }

  getMovementUserLabel(row: StockCellMovementRow) {
    return row.user?.name || row.user?.phone || 'Система';
  }

  getMovementActionLabel(row: StockCellMovementRow) {
    const note = String(row.note || '').toUpperCase();
    const fromType = row.fromCell?.type;
    const toType = row.toCell?.type;

    if (note === 'PICK' || (fromType === 'REAL' && toType === 'UNASSIGNED_OUT')) {
      return 'Снял с полки';
    }

    if (note === 'ABSOLUTE_CELL_RECONCILE') {
      return 'Переучет';
    }

    if (note === 'VIRTUAL_OFFSET_RESOLVE') {
      return 'Погасил встречные виртуальные остатки';
    }

    if (
      note === 'PLACE' ||
      (toType === 'REAL' && fromType && ['BUFFER', 'UNASSIGNED_OUT', 'REAL'].includes(fromType))
    ) {
      return 'Разместил';
    }

    return 'Системное движение';
  }

  getMovementSourceLabel(row: StockCellMovementRow) {
    return row.sourceType || 'Система';
  }

  async reloadMovementRows() {
    this.movementOffset = 0;
    await this.loadMovementRows(false);
  }

  async loadMoreMovements() {
    if (this.movementLoading || !this.canLoadMoreMovements) {
      return;
    }
    await this.loadMovementRows(true);
  }

  async submitAction() {
    if (!this.product?.id || !this.warehouse?.id || !this.canSubmitAction) {
      return;
    }

    this.loading = true;
    try {
      if (this.actionMode === 'PICK' && this.sourceCellId) {
        await firstValueFrom(
          this.warehouseService.pickStock({
            warehouseId: this.warehouse.id,
            productId: this.product.id,
            fromCellId: this.sourceCellId,
            qty: Number(this.actionQty),
            sourceType: 'catalog_manager_modal'
          })
        );
      }

      if (this.actionMode === 'PLACE' && this.targetCellId) {
        await firstValueFrom(
          this.warehouseService.placeStock({
            warehouseId: this.warehouse.id,
            productId: this.product.id,
            fromCellId: this.sourceCellId || null,
            toCellId: this.targetCellId,
            qty: Number(this.actionQty),
            sourceType: 'catalog_manager_modal'
          })
        );
      }

      if (this.actionMode === 'COUNT' && this.targetCellId) {
        await firstValueFrom(
          this.warehouseService.setCellStock({
            warehouseId: this.warehouse.id,
            productId: this.product.id,
            cellId: this.targetCellId,
            qty: Number(this.actionQty),
            sourceType: 'catalog_manager_modal'
          })
        );
      }

      await this.refreshProductState();
      await this.reloadMovementRows();
      this.notificationService.notice('Адресное хранение обновлено');
    } catch (error) {
      this.notificationService.warning(
        this.getErrorMessage(error, 'Не удалось обновить адресное хранение')
      );
    } finally {
      this.loading = false;
    }
  }

  async previewVirtualOffset() {
    if (!this.product?.id || !this.warehouse?.id || !this.canUseCountMode) {
      return;
    }

    this.virtualOffsetLoading = true;
    try {
      this.virtualOffsetPreview = await firstValueFrom(
        this.warehouseService.previewVirtualCellOffset({
          warehouseId: this.warehouse.id,
          productId: this.product.id
        })
      );
    } catch (error) {
      this.notificationService.warning(
        this.getErrorMessage(error, 'Не удалось проверить виртуальные остатки')
      );
    } finally {
      this.virtualOffsetLoading = false;
    }
  }

  cancelVirtualOffset() {
    this.virtualOffsetPreview = undefined;
  }

  async confirmVirtualOffset() {
    if (
      !this.product?.id ||
      !this.warehouse?.id ||
      !this.virtualOffsetPreview?.canResolve ||
      this.virtualOffsetLoading
    ) {
      return;
    }

    this.virtualOffsetLoading = true;
    try {
      const result = await firstValueFrom(
        this.warehouseService.resolveVirtualCellOffset({
          warehouseId: this.warehouse.id,
          productId: this.product.id
        })
      );
      this.virtualOffsetPreview = undefined;
      await this.refreshProductState();
      await this.reloadMovementRows();
      this.notificationService.notice(`Погашено встречных остатков: ${result.resolveQty}`);
    } catch (error) {
      this.notificationService.warning(
        this.getErrorMessage(error, 'Не удалось погасить виртуальные остатки')
      );
    } finally {
      this.virtualOffsetLoading = false;
    }
  }

  async createTargetCell() {
    const warehouseId = Number(this.warehouse?.id || 0);
    const code = this.targetCellQuery;
    if (!warehouseId || !code) {
      return this.notificationService.warning('Выберите склад и введите код полки');
    }

    const existing = this.findTargetOptionByCode(code);
    if (existing) {
      this.targetCellId = existing.id;
      this.targetCellControl.setValue(existing.code, { emitEvent: false });
      this.updateFilteredTargetOptions(existing.code);
      return;
    }

    this.creatingTargetCell = true;
    try {
      const created = await firstValueFrom(
        this.warehouseService.createCell({
          warehouseId,
          code
        })
      );
      this.targetCellId = created.id;
      this.targetCellControl.setValue(created.code, { emitEvent: false });
      await this.loadWarehouseCells();
      this.notificationService.notice(`Создана полка ${created.code}`);
    } catch (error) {
      this.notificationService.warning(this.getErrorMessage(error, 'Не удалось создать полку'));
    } finally {
      this.creatingTargetCell = false;
    }
  }

  private async loadWarehouseCells() {
    if (!this.warehouse?.id) return;
    this.warehouseCells = await firstValueFrom(
      this.warehouseService.searchCells({
        warehouseId: this.warehouse.id
      })
    );
    this.selectMode(this.actionMode);
    this.updateFilteredTargetOptions(this.targetCellControl.value);
  }

  private async refreshProductState() {
    if (!this.product?.id || !this.warehouse?.id) return;
    const fresh = await firstValueFrom(this.productService.getOne(this.product.id));
    Object.assign(this.product, fresh);
    this.virtualOffsetPreview = undefined;
    await this.loadWarehouseCells();
  }

  private applyRealtimeProductUpdate(product: Product) {
    if (!this.product) {
      return;
    }

    this.product = {
      ...this.product,
      ...product
    };
    this.virtualOffsetPreview = undefined;

    if (
      this.actionMode === 'PICK' &&
      !this.sourceOptions.some((row) => row.cellId === this.sourceCellId)
    ) {
      this.sourceCellId = this.sourceOptions.find((row) => row.qty > 0)?.cellId || null;
    }

    if (this.isTargetCellMode && !this.targetOptions.some((row) => row.id === this.targetCellId)) {
      this.targetCellId =
        this.targetOptions.find((row) => this.recommendedCells.includes(row.code))?.id ||
        this.targetOptions[0]?.id ||
        null;
      this.syncTargetCellControl();
    }
  }

  close() {
    this.dialogRef.close(this.product);
  }

  private async loadMovementRows(append: boolean) {
    if (!this.product?.id || !this.warehouse?.id) {
      this.movementRows = [];
      this.movementTotal = 0;
      this.movementOffset = 0;
      return;
    }

    const offset = append ? this.movementOffset : 0;
    this.movementLoading = true;
    try {
      const response = await firstValueFrom(
        this.warehouseService.searchCellMovements({
          productId: this.product.id,
          warehouseId: this.warehouse.id,
          limit: this.movementLimit,
          offset
        })
      );
      this.movementTotal = Number(response.length || 0);
      if (append) {
        const existingIds = new Set(this.movementRows.map((row) => row.id));
        this.movementRows = [
          ...this.movementRows,
          ...response.data.filter((row) => !existingIds.has(row.id))
        ];
      } else {
        this.movementRows = response.data;
      }
      this.movementOffset = this.movementRows.length;
    } catch (error) {
      this.notificationService.warning(this.getErrorMessage(error, 'Не удалось загрузить журнал'));
    } finally {
      this.movementLoading = false;
    }
  }

  private updateFilteredTargetOptions(value?: string | null) {
    const code = this.normalizeCellCode(value);
    this.filteredTargetOptions = this.targetOptions.filter((row) => {
      if (!code) return true;
      return row.code.includes(code);
    });
  }

  private syncTargetCellIdFromInput(value?: string | null) {
    if (!this.isTargetCellMode) {
      return;
    }

    const code = this.normalizeCellCode(value);
    if (!code) {
      this.targetCellId = null;
      return;
    }

    const match = this.findTargetOptionByCode(code);
    this.targetCellId = match?.id || null;
  }

  private syncTargetCellControl() {
    const selected = this.targetOptions.find((row) => row.id === this.targetCellId);
    this.targetCellControl.setValue(selected?.code || '', { emitEvent: false });
    this.updateFilteredTargetOptions(selected?.code || this.targetCellControl.value);
  }

  private findTargetOptionByCode(code: string) {
    const normalizedCode = this.normalizeCellCode(code);
    return this.targetOptions.find((row) => this.normalizeCellCode(row.code) === normalizedCode);
  }

  private normalizeCellCode(code: unknown) {
    return String(code || '')
      .trim()
      .replace(/^!+/, '')
      .replace(/\s+/g, ' ')
      .toUpperCase();
  }

  private getErrorMessage(error: unknown, fallbackMessage: string) {
    const payload = error as {
      error?: { message?: string | string[] };
      message?: string;
    };
    const message = payload?.error?.message || payload?.message;
    if (Array.isArray(message)) {
      return message.join(', ');
    }
    return message || fallbackMessage;
  }
}
